import AuthService, { localStorageKeys } from '@services/AuthService';
import axios from '@services/InterceptorsForAxiosRequests';
import { buildQueryString } from '@utils/Functions';
import { usersApi } from '@routes/api';

const AUTH_USER = localStorageKeys?.AUTH_USER;

const getUserFilters = (params = {}) => {
  const filters = {
    username_filter: params.userName?.trim(),
    page_size: params.pageSize,
    page_num: params.pageNumber,
    id_user: params.id_user,
    name: params.name?.trim(),
    gerencia: params.managments?.trim(),
    q: params.query?.trim(),
    indice: params.indice,
    lista: params.lista,
  };

  return buildQueryString(filters);
};

export const storage = globalThis.window?.localStorage ?? {};
class UserService {
  getCurrentUser() {
    const token = AuthService.getAuthHeader();
    const filters = buildQueryString({});
    const path = usersApi.userCurrent(filters);
    return axios.get(path, token);
  }

  getTareasPendientes() {
    const token = AuthService.getAuthHeader();

    const path = usersApi.tareasPendientes();
    return axios.get(path, token);
  }

  saveCurrentUserInLocalStorage = (userInfoResponse) => {
    if (!userInfoResponse || typeof userInfoResponse !== 'object') {
      console.warn('Invalid input:', userInfoResponse);
      return false;
    }
    const { data } = userInfoResponse;
    if (!data) {
      console.warn('Missing data field in:', userInfoResponse);
      return false;
    }
    try {
      storage.setItem(AUTH_USER, JSON.stringify(data));
      return userInfoResponse;
    } catch (e) {
      console.error(e);
      return false;
    }
  };

  autocompleteBoss(params) {
    const token = AuthService.getAuthHeader();
    const filters = buildQueryString({
      q: params.search,
      id_related_user: params.id_related_user,
      id_exclude: params.id_exclude,
    });
    const path = usersApi.autocompleteBoss(filters);
    return axios.get(path, token);
  }

  autocompleteCompartir(q) {
    const token = AuthService.getAuthHeader();
    const filters = buildQueryString({ q: q });
    const path = usersApi.autocompleteCompartir(filters);
    return axios.get(path, token);
  }

  autocompleteSubgerencias(gerenciaId) {
    const token = AuthService.getAuthHeader();
    const filters = buildQueryString({ gerencia_id: gerenciaId });
    const path = usersApi.autocompleteSubgerencias(filters);
    return axios.get(path, token);
  }

  autocompleteUser({ q }) {
    const token = AuthService.getAuthHeader();
    const filters = buildQueryString({ q: q });
    const path = usersApi.autocompleteUser(filters);
    return axios.get(path, token);
  }

  getBirthday({ rangeDays, boss }) {
    const token = AuthService.getAuthHeader();
    const filters = buildQueryString({ boss: boss, range: rangeDays });
    const path = usersApi.birthday(filters);
    return axios.get(path, token);
  }

  async shareLicenses({ permiso, usersSelected }) {
    const token = AuthService.getAuthHeader();
    const filters = buildQueryString({ permiso: permiso });
    const path = usersApi.compartidosBase(filters);
    const body = usersSelected;
    const response = await axios.patch(path, body, token);
    return response.data;
  }

  deleteShareUser(id) {
    const token = AuthService.getAuthHeader();
    const filters = buildQueryString({});
    const path = usersApi.compartidosPorId(id, filters);
    return axios.delete(path, token);
  }

  editUserPermissions(data) {
    const token = AuthService.getAuthHeader();
    const filters = buildQueryString({ permiso: data?.permission });
    const path = usersApi.compartidosPorIdPermiso(data?.id, filters);
    const body = {};
    return axios.patch(path, body, token);
  }

  compartidosViaticos(usersIds) {
    const token = AuthService.getAuthHeader();
    const filters = buildQueryString({});
    const path = usersApi.compartidosViaticos(filters);
    const body = usersIds;
    return axios.patch(path, body, token);
  }

  compartidosViaticosQuitarPermisos(userId) {
    const token = AuthService.getAuthHeader();
    const filters = buildQueryString({});
    const path = usersApi.compartidosViaticosQuitarPermisos(userId, filters);
    return axios.delete(path, token);
  }

  getDirectorio(params) {
    const token = AuthService.getAuthHeader();
    const filters = getUserFilters(params);
    const path = usersApi.directorioBase(filters);
    return axios.get(path, token);
  }

  addDirectorioFavorito(id) {
    const token = AuthService.getAuthHeader();
    const filters = buildQueryString({ id_user: id });
    const path = usersApi.directorioFavorito(filters);
    const body = {};
    return axios.patch(path, body, token);
  }

  sharePresenteeismIrregularities(body) {
    const token = AuthService.getAuthHeader();
    const path = usersApi.compartidoPresentismo();
    return axios.patch(path, body, token);
  }

  deleteSharedPresenteeismIrregularities(id) {
    const token = AuthService.getAuthHeader();
    const filters = buildQueryString({});
    const path = usersApi.eliminarCompartidoPresentismo(id, filters);
    return axios.delete(path, token);
  }

  removeDirectorioFavorito(id) {
    const token = AuthService.getAuthHeader();
    const filters = buildQueryString({ id_user: id });
    const path = usersApi.directorioFavorito(filters);
    return axios.delete(path, token);
  }

  getDirectorioGerencias() {
    const token = AuthService.getAuthHeader();
    const filters = getUserFilters({});
    const path = usersApi.directorioGerencias(filters);
    return axios.get(path, token);
  }

  getDirectorioIndice(params) {
    const token = AuthService.getAuthHeader();
    const filters = getUserFilters(params);
    const path = usersApi.directorioIndice(filters);
    return axios.get(path, token);
  }

  uploadProfileImage(body) {
    const token = AuthService.getAuthHeader();
    const filters = buildQueryString({});
    const path = usersApi.profileImage(filters);
    return axios.post(path, body, token);
  }

  getTopes(params) {
    const token = AuthService.getAuthHeader();
    const filters = buildQueryString({ q: params?.q });
    const path = usersApi.topes(filters);
    return axios.get(path, token);
  }

  getTuArsat() {
    const token = AuthService.getAuthHeader();
    const filters = buildQueryString({});
    const path = usersApi.tuArsat(filters);
    return axios.get(path, token);
  }

  async editTuArsatContact(body) {
    const token = AuthService.getAuthHeader();
    const filters = buildQueryString({});
    const path = usersApi.tuArsatContacto(filters);
    const response = await axios.patch(path, body, token);
    return response.data;
  }

  async updateWorkplace(body) {
    const token = AuthService.getAuthHeader();
    const filters = buildQueryString({});
    const path = usersApi.workplaceBase(filters);
    const response = await axios.patch(path, body, token);
    return response.data;
  }

  getWorkplaceFirstOption() {
    const token = AuthService.getAuthHeader();
    const filters = buildQueryString({});
    const path = usersApi.workplaceOption1(filters);
    return axios.get(path, token);
  }

  getWorkplaceSecondOption(id) {
    const token = AuthService.getAuthHeader();
    const filters = buildQueryString({ id_lugar_trabajo: id });
    const path = usersApi.workplaceOption2(filters);
    return axios.get(path, token);
  }

  getWorkplaceThirdOption(id) {
    const token = AuthService.getAuthHeader();
    const filters = buildQueryString({ id_detalle: id });
    const path = usersApi.workplaceOption3(filters);
    return axios.get(path, token);
  }

  getQrEmployeeUrl() {
    const token = AuthService.getAuthHeaderMultipartBlob();
    const path = usersApi.getQrUrl();
    return axios.get(path, token);
  }
  getQrEmployeeVcard(id_user) {
    const token = AuthService.getAuthHeader();
    const path = usersApi.getQrVcard(id_user);
    return axios.get(path, token);
  }

  getHijos() {
    const token = AuthService.getAuthHeader();
    const filters = buildQueryString({});
    const path = usersApi.autocompleteHijos(filters);
    return axios.get(path, token);
  }
}

export default new UserService();
